<template>
	<div class="import-page">
		<!--顶部标题-->
		<div class="page-header">
			<div class="page-title">
				<el-icon size="22"><UploadFilled /></el-icon>
				<span>批量导入订单</span>
			</div>
			<div class="page-subtitle">上传本地表格 → 表头匹配列 → 预览确认 → 一键批量创建订单</div>
		</div>

		<!--发件人信息（所有导入订单共用）-->
		<div class="card">
			<div class="card-header">
				<span class="card-index sender-icon"><el-icon><Position /></el-icon></span>
				<span class="card-title">发件人信息</span>
				<span class="card-extra">所有导入订单共用此发件人</span>
			</div>
			<el-form label-position="top" class="sender-form" style="width: 40%;">
				<div class="sender-grid">
					<el-form-item label="姓名">
						<el-input v-model="senderForm.name" placeholder="发件人名称" clearable></el-input>
					</el-form-item>
					<el-form-item label="电话">
						<el-input v-model="senderForm.mobile" placeholder="手机或座机号码" clearable></el-input>
					</el-form-item>
				</div>
				<el-form-item label="省市区">
					<el-cascader v-model="senderForm.area_code" ref="sender_area" @change="sender_area_change"
						separator=" - " placeholder="请选择发件方所在省市区信息" :options="areaData"
						:props="{label:'name',value:'adcode',children:'districts'}" clearable filterable
						style="width: 100%;"></el-cascader>
				</el-form-item>
				<el-form-item label="详细地址">
					<el-input v-model="senderForm.address" placeholder="请输入发件方详细地址" clearable></el-input>
				</el-form-item>
			</el-form>
		</div>

		<!--选择本地表格-->
		<div class="card">
			<div class="card-header">
				<span class="card-index">1</span>
				<span class="card-title">选择本地表格</span>
				<span class="card-extra">支持 .xlsx / .xls / .csv</span>
			</div>
			<input type="file" ref="fileInput" accept=".xlsx,.xls,.csv" style="display: none;" @change="handleFileChange" />
			<div class="upload-zone" :class="{ 'drag-over': dragOver }" @click="$refs.fileInput.click()"
				@dragover.prevent="dragOver = true" @dragleave.prevent="dragOver = false"
				@drop.prevent="handleDrop">
				<el-icon class="upload-icon"><UploadFilled /></el-icon>
				<template v-if="!fileName">
					<div class="upload-main">点击选择或拖拽表格文件到此处</div>
					<div class="upload-tip">上传后将弹出预览窗口，在表头中选择对应列即可匹配</div>
				</template>
				<template v-else>
					<div class="upload-main file-name">
						<el-icon><Document /></el-icon>
						<span>{{ fileName }}</span>
						<span class="file-size">{{ fileSize }}</span>
					</div>
					<div class="upload-tip">点击可重新选择文件</div>
				</template>
			</div>
			<div v-if="fileName" class="upload-actions">
				<el-button type="primary" @click="openPreviewDialog" :icon="View">查看表格 / 匹配列</el-button>
				<el-button @click="resetAll" :icon="Refresh">重新选择</el-button>
			</div>
		</div>

		<!--订单预览与导入-->
		<div v-if="previewOrders.length > 0" class="card">
			<div class="card-header">
				<span class="card-index">2</span>
				<span class="card-title">订单预览</span>
				<span class="card-extra">
					共 {{ previewOrders.length }} 行
					<span v-if="invalidCount > 0" class="invalid-tip">（{{ invalidCount }} 行信息不完整，无法勾选）</span>
				</span>
			</div>
			<el-table :data="previewOrders" height="48vh" ref="previewTable" border stripe
				@selection-change="handleSelectionChange" :row-class-name="previewRowClassName"
				:header-cell-style="{background:'#f5f7fa',color:'#3b3b3b','font-weight': 600}">
				<el-table-column type="selection" width="45" align="center" :selectable="row => row.valid && !row.imported"></el-table-column>
				<el-table-column type="index" label="#" width="45" align="center"></el-table-column>
				<el-table-column prop="recipient.name" label="收件人姓名" align="center" min-width="100" show-overflow-tooltip></el-table-column>
				<el-table-column prop="recipient.mobile" label="电话" align="center" min-width="115"></el-table-column>
				<el-table-column label="收件地址" align="left" min-width="230">
					<template #default="scope">
						<div class="addr-cell">
							<div v-if="scope.row.recipient.provinceName" class="addr-region">
								{{ scope.row.recipient.provinceName }} {{ scope.row.recipient.cityName }} {{ scope.row.recipient.districtName }}
							</div>
							<div class="addr-detail">{{ scope.row.recipient.address }}</div>
						</div>
					</template>
				</el-table-column>
				<el-table-column prop="goods[0].name" label="物品名称" align="center" min-width="100" show-overflow-tooltip></el-table-column>
				<el-table-column prop="goods[0].quantity" label="数量" align="center" width="70"></el-table-column>
				<el-table-column prop="goods[0].weight" label="重量(KG)" align="center" width="90"></el-table-column>
				<el-table-column prop="remark" label="备注" align="center" min-width="110" show-overflow-tooltip></el-table-column>
				<el-table-column label="状态" align="center" width="100">
					<template #default="scope">
						<el-tag v-if="scope.row.imported" type="success" size="small" effect="dark">已导入</el-tag>
						<el-tag v-else-if="!scope.row.valid" type="warning" size="small" effect="plain">信息不完整</el-tag>
						<el-tag v-else type="success" size="small" effect="plain">可导入</el-tag>
					</template>
				</el-table-column>
			</el-table>
			<div class="import-bar">
				<div class="import-bar-left">
					<el-button @click="toggleSelectAll" :disabled="previewOrders.length == 0">全选/取消</el-button>
					<span class="selected-count">
						已选 <b class="selected-num">{{ selectedOrders.length }}</b> 单
					</span>
				</div>
				<div class="import-bar-right">
					<el-button type="primary" size="large" :disabled="selectedOrders.length == 0 || importing"
						:loading="importing" @click="importOrders">
						导入选中订单
					</el-button>
				</div>
			</div>
		</div>

		<!--表格预览与列匹配弹窗-->
		<el-dialog v-model="previewDialog" :title="'表格预览与列匹配 - ' + fileName" width="80%" top="5vh"
			destroy-on-close append-to-body>
			<div class="dialog-tip">
				<el-icon><InfoFilled /></el-icon>
				<span>在每列表头中选择该列对应的字段（收件人姓名、电话、详细地址为必选；省/市/区可选，未选时将从详细地址自动解析）</span>
			</div>
			<el-table :data="previewRows" size="small" max-height="52vh" border
				:header-cell-style="{background:'#f5f7fa',color:'#3b3b3b','font-weight': 600}">
				<el-table-column type="index" label="#" width="45" align="center"></el-table-column>
				<el-table-column v-for="(col, idx) in columns" :key="idx" align="center" min-width="140">
					<template #header>
						<div class="header-mapping">
							<div class="header-name">{{ col.label }}</div>
							<el-select v-model="columnMapping[idx]" placeholder="选择字段" clearable filterable size="small"
								@change="(val) => handleMappingChange(val, idx)">
								<el-option v-for="f in mappingFields" :key="f.key" :label="f.label" :value="f.key">
									<span>{{ f.label }}</span>
									<span v-if="f.required" class="opt-required">必选</span>
								</el-option>
							</el-select>
						</div>
					</template>
					<template #default="scope">
						<span :class="{'cell-empty': scope.row[idx] === '' || scope.row[idx] === null || scope.row[idx] === undefined}">{{ scope.row[idx] }}</span>
					</template>
				</el-table-column>
			</el-table>
			<template #footer>
				<div class="dialog-footer">
					<span class="footer-hint">带 <span class="required">*</span> 为必选项；数量未匹配时默认为 1，重量未匹配时默认为 1 KG</span>
					<div>
						<el-button @click="resetMapping" :icon="Refresh">重置匹配</el-button>
						<el-button @click="previewDialog = false">取消</el-button>
						<el-button type="primary" @click="generatePreview" :icon="MagicStick">生成订单预览</el-button>
					</div>
				</div>
			</template>
		</el-dialog>
	</div>
</template>

<script>
	import {
		ElMessage,
		ElLoading
	} from 'element-plus'
	import XLSX from 'xlsx/dist/xlsx.full.min.js'
	import areaData from "@/assets/areaCode.json";
	export default {
		name: 'orderImport',
		data() {
			return {
				areaData: areaData,
				//预览弹窗开关
				previewDialog: false,
				//拖拽中
				dragOver: false,
				//发件人信息（导入订单共用）
				senderForm: {
					name: "",
					mobile: "",
					area_code: [],
					provinceName: "",
					cityName: "",
					districtName: "",
					address: "",
				},
				fileName: "",
				fileSize: "",
				allRows: [], //原始表格全部行（二维数组）
				hasHeader: true, //首行是否为表头
				columns: [], //列信息 [{index, label}]
				previewRows: [], //预览前几行
				//列匹配：表格列序号 -> 字段key
				columnMapping: {},
				//预览订单
				previewOrders: [],
				//勾选的订单
				selectedOrders: [],
				//是否正在导入
				importing: false
			}
		},
		computed: {
			//列匹配字段配置
			mappingFields() {
				return [{
						key: 'name',
						label: '收件人姓名',
						icon: 'User',
						required: true
					},
					{
						key: 'phone',
						label: '电话',
						icon: 'Phone',
						required: true
					},
					{
						key: 'province',
						label: '省份',
						icon: 'MapLocation',
						required: false
					},
					{
						key: 'city',
						label: '城市',
						icon: 'OfficeBuilding',
						required: false
					},
					{
						key: 'district',
						label: '区县',
						icon: 'LocationInformation',
						required: false
					},
					{
						key: 'address',
						label: '详细地址',
						icon: 'Location',
						required: true
					},
					{
						key: 'goodsName',
						label: '物品名称',
						icon: 'Goods',
						required: false
					},
					{
						key: 'quantity',
						label: '数量',
						icon: 'Box',
						required: false
					},
					{
						key: 'weight',
						label: '重量(KG)',
						icon: 'ScaleToOriginal',
						required: false
					},
					{
						key: 'remark',
						label: '备注',
						icon: 'Message',
						required: false
					}
				]
			},
			//信息不完整的行数
			invalidCount() {
				return this.previewOrders.filter(o => !o.valid).length
			}
		},
		mounted() {
			//默认发件人信息
			var default_sender_info = JSON.parse(localStorage.getItem('default_sender_info'));
			if (default_sender_info) {
				this.senderForm = default_sender_info;
			}
		},
		methods: {
			//省市区联级选择器（发件人）
			sender_area_change() {
				const checkedNodes = this.$refs.sender_area.getCheckedNodes()
				if (checkedNodes && checkedNodes.length > 0) {
					this.senderForm.provinceName = checkedNodes[0].pathLabels[0] //省级
					this.senderForm.cityName = checkedNodes[0].pathLabels[1] //市级
					this.senderForm.districtName = checkedNodes[0].pathLabels[2] //区县级
				} else {
					this.senderForm.provinceName = ""
					this.senderForm.cityName = ""
					this.senderForm.districtName = ""
				}
			},
			//文件选择
			handleFileChange(e) {
				var file = e.target.files[0]
				if (file) this.parseFile(file)
				//清空input，允许重复选择同一文件
				e.target.value = ''
			},
			//拖拽文件
			handleDrop(e) {
				this.dragOver = false
				var file = e.dataTransfer.files && e.dataTransfer.files[0]
				if (file) this.parseFile(file)
			},
			//解析本地表格文件
			parseFile(file) {
				this.fileName = file.name
				this.fileSize = this.formatSize(file.size)
				const reader = new FileReader()
				reader.onload = (ev) => {
					try {
						const data = new Uint8Array(ev.target.result)
						const workbook = XLSX.read(data, {
							type: 'array'
						})
						const sheet = workbook.Sheets[workbook.SheetNames[0]]
						const rows = XLSX.utils.sheet_to_json(sheet, {
							header: 1,
							defval: ''
						})
						if (!rows || rows.length == 0) {
							ElMessage.error('表格内容为空，请重新选择');
							return
						}
						this.allRows = rows
						this.hasHeader = true
						this.rebuildPreview()
						this.autoDetectMapping()
						this.previewOrders = []
						this.selectedOrders = []
						//识别后弹出预览匹配窗口
						this.openPreviewDialog()
					} catch (err) {
						console.error(err)
						this.fileName = ""
						this.fileSize = ""
						ElMessage.error('表格解析失败，请确认文件格式正确');
					}
				}
				reader.readAsArrayBuffer(file)
			},
			//文件大小格式化
			formatSize(bytes) {
				if (!bytes && bytes !== 0) return ''
				if (bytes < 1024) return bytes + ' B'
				if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
				return (bytes / 1024 / 1024).toFixed(2) + ' MB'
			},
			//打开预览匹配弹窗
			openPreviewDialog() {
				if (this.allRows.length == 0) return
				this.rebuildPreview()
				this.previewDialog = true
			},
			//根据是否首行为表头重建列信息与预览行
			rebuildPreview() {
				var headerRow = this.hasHeader ? this.allRows[0] : null
				var dataStart = this.hasHeader ? 1 : 0
				var colCount = Math.max(...this.allRows.map(r => r.length))
				this.columns = []
				for (var i = 0; i < colCount; i++) {
					var headerVal = headerRow && headerRow[i] !== undefined && headerRow[i] !== '' ? String(
						headerRow[i]).trim() : ''
					this.columns.push({
						index: i,
						label: (headerVal ? headerVal + '（' : '') + this.colLetter(i) + (headerVal ? '）' : '')
					})
				}
				this.previewRows = this.allRows.slice(dataStart, dataStart + 5)
			},
			//列字母：0->A, 25->Z, 26->AA
			colLetter(index) {
				var letters = ''
				var i = index + 1
				while (i > 0) {
					var rem = (i - 1) % 26
					letters = String.fromCharCode(65 + rem) + letters
					i = Math.floor((i - 1) / 26)
				}
				return letters
			},
			//根据表头关键字自动匹配列（列序号 -> 字段key）
			autoDetectMapping() {
				var headers = this.allRows[0] || []
				var rule = [{
						key: 'name',
						keywords: ['姓名', '收件人', '名字', '联系人']
					},
					{
						key: 'phone',
						keywords: ['电话', '手机', '联系方式', '号码']
					},
					{
						key: 'province',
						keywords: ['省份', '省', 'province']
					},
					{
						key: 'city',
						keywords: ['城市', '市', 'city']
					},
					{
						key: 'district',
						keywords: ['区县', '区', '县', 'district']
					},
					{
						key: 'address',
						keywords: ['详细地址', '地址', 'address']
					},
					{
						key: 'goodsName',
						keywords: ['物品', '商品', '货物', '品名']
					},
					{
						key: 'quantity',
						keywords: ['数量', '件数']
					},
					{
						key: 'weight',
						keywords: ['重量', '重']
					},
					{
						key: 'remark',
						keywords: ['备注', '留言', '说明']
					}
				]
				var used = []
				for (var r = 0; r < rule.length; r++) {
					var matched = null
					for (var i = 0; i < headers.length; i++) {
						if (used.indexOf(i) != -1) continue
						var text = String(headers[i] || '')
						for (var k = 0; k < rule[r].keywords.length; k++) {
							if (text.indexOf(rule[r].keywords[k]) != -1) {
								matched = i
								break
							}
						}
						if (matched !== null) break
					}
					if (matched !== null) {
						this.columnMapping[matched] = rule[r].key
						used.push(matched)
					}
				}
			},
			//表头选择变化：同一字段只允许选择一列
			handleMappingChange(val, colIdx) {
				if (!val) return
				for (var key in this.columnMapping) {
					if (key != colIdx && this.columnMapping[key] == val) {
						this.columnMapping[key] = null
						ElMessage.warning('字段「' + this.fieldLabel(val) + '」已分配给其他列，已自动取消原列');
						break
					}
				}
			},
			//字段key转显示名称
			fieldLabel(key) {
				var fields = this.mappingFields
				for (var i = 0; i < fields.length; i++) {
					if (fields[i].key == key) return fields[i].label
				}
				return key
			},
			//获取某个字段对应的列序号
			fieldColumn(key) {
				for (var colIdx in this.columnMapping) {
					if (this.columnMapping[colIdx] == key) return parseInt(colIdx)
				}
				return null
			},
			//重置列匹配
			resetMapping() {
				this.columnMapping = {}
			},
			//全部重置（重新选择文件）
			resetAll() {
				this.fileName = ""
				this.fileSize = ""
				this.allRows = []
				this.columns = []
				this.previewRows = []
				this.columnMapping = {}
				this.previewOrders = []
				this.selectedOrders = []
				this.previewDialog = false
			},
			//根据列匹配生成订单预览
			generatePreview() {
				var nameCol = this.fieldColumn('name')
				var phoneCol = this.fieldColumn('phone')
				var addressCol = this.fieldColumn('address')
				if (nameCol === null || phoneCol === null || addressCol === null) {
					ElMessage.error('请先在表头中选择收件人姓名、电话、详细地址对应的列');
					return
				}
				var dataStart = this.hasHeader ? 1 : 0
				var rows = this.allRows.slice(dataStart)
				var orders = []
				for (var i = 0; i < rows.length; i++) {
					var row = rows[i]
					var getVal = (colIdx) => {
						if (colIdx === null || colIdx === undefined) return ''
						var v = row[colIdx]
						return (v === undefined || v === null) ? '' : String(v).trim()
					}
					var provinceName = getVal(this.fieldColumn('province'))
					var cityName = getVal(this.fieldColumn('city'))
					var districtName = getVal(this.fieldColumn('district'))
					var detailAddress = getVal(addressCol)
					//省市区分别匹配时，用独立列的值解析区划编码；否则从完整地址解析
					var addr
					if (provinceName || cityName || districtName) {
						addr = this.matchArea(provinceName, cityName, districtName)
						addr.address = detailAddress
					} else {
						addr = this.parseAddress(detailAddress)
					}
					var quantity = getVal(this.fieldColumn('quantity'))
					var weight = parseFloat(getVal(this.fieldColumn('weight')))
					var fullAddress = (addr.provinceName || '') + (addr.cityName || '') + (addr.districtName || '') + (addr
						.address || '')
					orders.push({
						recipient: {
							name: getVal(nameCol),
							mobile: getVal(phoneCol),
							area_code: addr.area_code,
							provinceName: addr.provinceName,
							cityName: addr.cityName,
							districtName: addr.districtName,
							address: addr.address
						},
						goods: [{
							name: getVal(this.fieldColumn('goodsName')) || '日用品',
							quantity: quantity || '1',
							weight: (isNaN(weight) || weight <= 0) ? 1 : weight
						}],
						remark: getVal(this.fieldColumn('remark')),
						valid: !!(getVal(nameCol) && getVal(phoneCol) && (fullAddress || detailAddress)),
						imported: false
					})
				}
				this.previewOrders = orders
				this.selectedOrders = []
				this.previewDialog = false
				if (this.previewOrders.length == 0) {
					ElMessage.error('未匹配到任何数据行，请检查列匹配设置');
					return
				}
				ElMessage.success('已生成 ' + this.previewOrders.length + ' 条订单预览');
			},
			//根据独立省/市/区列的值匹配区划编码
			matchArea(provinceName, cityName, districtName) {
				var province = null,
					city = null,
					district = null
				//匹配省级
				for (var i = 0; i < this.areaData.length; i++) {
					var p = this.areaData[i]
					if (provinceName && (provinceName.indexOf(p.name) != -1 || p.name.indexOf(provinceName) != -1)) {
						province = p
						break
					}
				}
				//匹配市级
				if (province && cityName) {
					for (var j = 0; j < (province.districts || []).length; j++) {
						var c = province.districts[j]
						if (cityName.indexOf(c.name) != -1 || c.name.indexOf(cityName) != -1) {
							city = c
							break
						}
					}
				}
				//市级未在省内匹配到时，尝试全局匹配市级
				if (!city && cityName) {
					for (var i2 = 0; i2 < this.areaData.length; i2++) {
						var p2 = this.areaData[i2]
						for (var j2 = 0; j2 < (p2.districts || []).length; j2++) {
							var c2 = p2.districts[j2]
							if (cityName.indexOf(c2.name) != -1 || c2.name.indexOf(cityName) != -1) {
								province = p2
								city = c2
								break
							}
						}
						if (city) break
					}
				}
				//匹配区县级
				if (city && districtName) {
					for (var k = 0; k < (city.districts || []).length; k++) {
						var d = city.districts[k]
						if (districtName.indexOf(d.name) != -1 || d.name.indexOf(districtName) != -1) {
							district = d
							break
						}
					}
				}
				var area_code = []
				if (province) {
					area_code.push(province.adcode)
					area_code.push(city ? city.adcode : province.adcode)
					area_code.push(district ? district.adcode : (city ? city.adcode : province.adcode))
				}
				return {
					area_code: area_code,
					provinceName: province ? province.name : provinceName,
					cityName: city ? city.name : cityName,
					districtName: district ? district.name : districtName
				}
			},
			//从完整地址中解析省市区
			parseAddress(fullAddress) {
				var address = String(fullAddress || '').trim()
				if (!address) {
					return {
						area_code: [],
						provinceName: '',
						cityName: '',
						districtName: '',
						address: ''
					}
				}
				var province = null,
					city = null,
					district = null
				//匹配省级
				for (var i = 0; i < this.areaData.length; i++) {
					var p = this.areaData[i]
					if (address.indexOf(p.name) === 0) {
						province = p
						break
					}
				}
				//省级未匹配时，尝试直接匹配市级（地址可能以市级开头）
				if (!province) {
					for (var i2 = 0; i2 < this.areaData.length; i2++) {
						var p2 = this.areaData[i2]
						for (var j = 0; j < (p2.districts || []).length; j++) {
							var c2 = p2.districts[j]
							if (address.indexOf(c2.name) === 0) {
								province = p2
								city = c2
								break
							}
						}
						if (city) break
					}
				}
				//匹配市级
				if (province && !city) {
					for (var j2 = 0; j2 < (province.districts || []).length; j2++) {
						var c3 = province.districts[j2]
						if (address.indexOf(c3.name) !== -1) {
							city = c3
							break
						}
					}
				}
				//匹配区县级
				if (city) {
					for (var k = 0; k < (city.districts || []).length; k++) {
						var d = city.districts[k]
						if (address.indexOf(d.name) !== -1) {
							district = d
							break
						}
					}
				}
				//去除已匹配的省市区名称，得到详细地址
				var detail = address
				if (province) detail = detail.replace(province.name, '')
				if (city && city.name != province.name) detail = detail.replace(city.name, '')
				if (district && district.name != city.name) detail = detail.replace(district.name, '')
				detail = detail.replace(/^[\s\-—,，、]+/, '').trim()

				var area_code = []
				if (province) {
					area_code.push(province.adcode)
					area_code.push(city ? city.adcode : province.adcode)
					area_code.push(district ? district.adcode : (city ? city.adcode : province.adcode))
				}
				return {
					area_code: area_code,
					provinceName: province ? province.name : '',
					cityName: city ? city.name : '',
					districtName: district ? district.name : '',
					address: detail || fullAddress
				}
			},
			//勾选变化
			handleSelectionChange(selection) {
				this.selectedOrders = selection
			},
			//全选/取消
			toggleSelectAll() {
				var table = this.$refs.previewTable
				if (!table) return
				var selectableRows = this.previewOrders.filter(row => row.valid && !row.imported)
				var allSelected = selectableRows.length > 0 && this.selectedOrders.length == selectableRows.length
				if (allSelected) {
					table.clearSelection()
				} else {
					selectableRows.forEach((row) => {
						table.toggleRowSelection(row, true)
					})
				}
			},
			//行样式：信息不完整的行标黄
			previewRowClassName({
				row
			}) {
				return row.valid || row.imported ? '' : 'row-invalid'
			},
			//导入勾选的订单
			async importOrders() {
				if (this.selectedOrders.length == 0) {
					ElMessage.error('请先勾选需要导入的订单');
					return
				}
				if (!this.senderForm.name || !this.senderForm.mobile || !this.senderForm.address) {
					ElMessage.error('请先完善发件人信息（姓名、电话、详细地址）');
					return
				}
				//保存默认发件人信息
				localStorage.setItem('default_sender_info', JSON.stringify(this.senderForm))
				this.importing = true
				var successCount = 0
				var failList = []
				const loading = ElLoading.service({
					lock: true,
					text: '正在导入订单...',
					background: 'rgba(0, 0, 0, 0.7)',
				})
				for (var i = 0; i < this.selectedOrders.length; i++) {
					var order = this.selectedOrders[i]
					if (!order.valid) {
						failList.push('第' + (i + 1) + '单：缺少收件人姓名/电话/地址')
						continue
					}
					var createForm = {
						sender: JSON.parse(JSON.stringify(this.senderForm)),
						recipient: order.recipient,
						goods: order.goods,
						service: [],
						remark: order.remark,
						source: "KD_WEB"
					}
					try {
						var res = await this.apiService.post({
							modules: "createOrder",
							content: createForm
						})
						if (res && res.status) {
							successCount++
							order.imported = true
						} else {
							failList.push('第' + (i + 1) + '单（' + (order.recipient.name || '') + '）：' + (res && res.msg ? res
								.msg : '创建失败'))
						}
					} catch (e) {
						console.error(e)
						failList.push('第' + (i + 1) + '单（' + (order.recipient.name || '') + '）：请求异常')
					}
				}
				loading.close()
				this.importing = false
				//清除已导入订单的勾选
				if (this.$refs.previewTable) this.$refs.previewTable.clearSelection()
				ElMessage.success('导入完成：成功' + successCount + '单' + (failList.length > 0 ? '，失败' + failList.length +
					'单' : ''))
				if (failList.length > 0) {
					var failHtml = ''
					for (var f = 0; f < failList.length; f++) {
						failHtml = failHtml + '<div>' + failList[f] + '</div>'
					}
					this.$alert(failHtml, '导入失败明细', {
						dangerouslyUseHTMLString: true
					})
				}
			}
		}
	}
</script>

<style scoped>
	.import-page {
		font-family: sans-serif;
	}

	/*顶部标题*/
	.page-header {
		background: linear-gradient(135deg, #007aff 0%, #4f9dff 100%);
		border-radius: 10px;
		color: #fff;
		padding: 16px 24px;
		display: flex;
		align-items: center;
		gap: 14px;
		box-shadow: 0 2px 8px rgba(0, 122, 255, 0.25);
	}

	.page-title {
		display: flex;
		align-items: center;
		gap: 8px;
		font-size: 18px;
		font-weight: 600;
	}

	.page-subtitle {
		font-size: 13px;
		opacity: 0.85;
		border-left: 1px solid rgba(255, 255, 255, 0.4);
		padding-left: 14px;
	}

	/*卡片*/
	.card {
		background: #fff;
		border-radius: 10px;
		padding: 18px 22px;
		margin-top: 14px;
		box-shadow: 0 1px 4px rgba(0, 21, 41, 0.06);
	}

	.card-header {
		display: flex;
		align-items: center;
		gap: 10px;
		margin-bottom: 16px;
	}

	.card-index {
		width: 24px;
		height: 24px;
		border-radius: 50%;
		background: #007aff;
		color: #fff;
		font-size: 13px;
		font-weight: 600;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}

	.card-index.sender-icon {
		background: #007aff;
	}

	.card-title {
		font-weight: 600;
		color: #303133;
		font-size: 15px;
	}

	.card-extra {
		margin-left: auto;
		color: #909399;
		font-size: 12px;
	}

	.invalid-tip {
		color: #e6a23c;
	}

	/*上传区域*/
	.upload-zone {
		border: 1.5px dashed #c0c4cc;
		border-radius: 10px;
		padding: 36px 20px;
		text-align: center;
		cursor: pointer;
		transition: all 0.2s;
		background: #fafbfc;
	}

	.upload-zone:hover,
	.upload-zone.drag-over {
		border-color: #007aff;
		background: #f0f7ff;
	}

	.upload-icon {
		font-size: 42px;
		color: #a8abb2;
		transition: color 0.2s;
	}

	.upload-zone:hover .upload-icon,
	.upload-zone.drag-over .upload-icon {
		color: #007aff;
	}

	.upload-main {
		font-size: 15px;
		color: #303133;
		margin-top: 10px;
	}

	.upload-tip {
		font-size: 12px;
		color: #909399;
		margin-top: 6px;
	}

	.file-name {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		color: #007aff;
		font-weight: 600;
	}

	.file-size {
		color: #909399;
		font-size: 12px;
		font-weight: 400;
	}

	.upload-actions {
		margin-top: 14px;
		display: flex;
		align-items: center;
		gap: 10px;
	}

	/*发件人信息*/
	.sender-grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 0 18px;
	}

	/*预览表格空单元格*/
	.cell-empty {
		color: #c0c4cc;
	}

	/*弹窗提示*/
	.dialog-tip {
		display: flex;
		align-items: center;
		gap: 6px;
		color: #909399;
		font-size: 13px;
		margin-bottom: 12px;
	}

	.dialog-tip .el-icon {
		color: #007aff;
		font-size: 16px;
	}

	/*表头列匹配*/
	.header-mapping {
		display: flex;
		flex-direction: column;
		gap: 6px;
		align-items: center;
	}

	.header-name {
		font-size: 12px;
		font-weight: 600;
		color: #3b3b3b;
		white-space: nowrap;
	}

	.header-mapping .el-select {
		width: 100%;
	}

	.opt-required {
		float: right;
		color: #f56c6c;
		font-size: 12px;
		margin-left: 12px;
	}

	.required {
		color: #f56c6c;
	}

	.dialog-footer {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 10px;
		flex-wrap: wrap;
	}

	.footer-hint {
		color: #909399;
		font-size: 12px;
	}

	/*订单预览地址列*/
	.addr-cell {
		line-height: 1.5;
	}

	.addr-region {
		color: #007aff;
		font-size: 12px;
		margin-bottom: 2px;
	}

	.addr-detail {
		color: #606266;
	}

	/*信息不完整行*/
	:deep(.el-table .row-invalid) {
		background-color: #fdf6ec !important;
	}

	:deep(.el-table .row-invalid:hover > td) {
		background-color: #f8ecd8 !important;
	}

	/*导入栏*/
	.import-bar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-top: 16px;
		padding-top: 14px;
		border-top: 1px solid #f0f0f0;
	}

	.selected-count {
		color: #606266;
		margin-left: 12px;
		font-size: 13px;
	}

	.selected-num {
		color: #007aff;
		font-size: 16px;
	}
</style>
